import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import api from '../lib/api';
import { TaskDto, TaskStatus, TaskType, TaskFilterDto } from '@prometeus/contracts';
import { TaskFormModal } from '../components/TaskFormModal';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { usePermissionGuard } from '../hooks/usePermissionGuard';

export const Route = createFileRoute('/w/$workspaceId/tasks')({
  component: WorkspaceTasksPage,
});

function WorkspaceTasksPage() {
  const { workspaceId } = Route.useParams();
  const queryClient = useQueryClient();
  const { requirePermission } = usePermissionGuard();
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskDto | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskDto | null>(null);
  const [myAgenda, setMyAgenda] = useState(true);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<TaskType | ''>('');
  const [searchFilter, setSearchFilter] = useState('');

  const filters: TaskFilterDto = useMemo(() => ({
    status: statusFilter || undefined,
    type: typeFilter || undefined,
    search: searchFilter || undefined,
  }), [statusFilter, typeFilter, searchFilter]);

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const response = await api.get('/members');
      return response.data.data;
    },
  });

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks', workspaceId, statusFilter, typeFilter, searchFilter, myAgenda],
    queryFn: async () => {
      const response = await api.get('/tasks', {
        params: filters,
      });
      return response.data.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tasks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] });
    },
  });

  const getStatusBadgeColor = (status: TaskStatus) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'BLOCKED':
        return 'bg-red-100 text-red-800';
      case 'ON_HOLD':
        return 'bg-yellow-100 text-yellow-800';
      case 'BACKLOG':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELED':
        return 'bg-gray-100 text-gray-500';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeColor = (type: TaskType) => {
    switch (type) {
      case 'HEARING':
        return 'bg-purple-100 text-purple-800';
      case 'EVENT':
        return 'bg-indigo-100 text-indigo-800';
      case 'DEADLINE':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTaskDate = (task: TaskDto) => {
    return task.dueDate || task.startAt;
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
          <button
            onClick={() => {
              if (!requirePermission('tasks.create', 'Criar tarefas')) return;
              setEditingTask(null);
              setShowModal(true);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            + Nova Task
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="myAgenda"
                checked={myAgenda}
                onChange={(e) => setMyAgenda(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="myAgenda" className="text-sm font-medium text-gray-700">
                Minha Agenda
              </label>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TaskStatus | '')}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todos os Status</option>
              <option value="BACKLOG">Aguardando</option>
              <option value="IN_PROGRESS">Em Execução</option>
              <option value="BLOCKED">Bloqueada</option>
              <option value="ON_HOLD">Suspensa</option>
              <option value="DONE">Concluída</option>
              <option value="CANCELED">Cancelada</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TaskType | '')}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todos os Tipos</option>
              <option value="TASK">Tarefa</option>
              <option value="DEADLINE">Prazo</option>
              <option value="EVENT">Evento</option>
              <option value="HEARING">Audiência</option>
            </select>

            <input
              type="text"
              placeholder="Buscar por título..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-4">
          {tasks && tasks.length > 0 ? (
            tasks.map((task: TaskDto) => {
              const assigneeMember = members?.find((m: any) => m.id === task.assigneeMemberId);
              const taskDate = getTaskDate(task);
              const isOverdue =
                taskDate &&
                new Date(taskDate) < new Date() &&
                task.status !== 'DONE' &&
                task.status !== 'CANCELED';

              return (
                <div
                  key={task.id}
                  className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    if (!requirePermission('tasks.read', 'Ver tarefas')) return;
                    setSelectedTask(task);
                    setShowDetailModal(true);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(
                          task.status
                        )}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeBadgeColor(
                          task.type
                        )}`}>
                          {task.type}
                        </span>
                        {isOverdue && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            ATRASADA
                          </span>
                        )}
                      </div>

                      {task.description && (
                        <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                      )}

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {assigneeMember && (
                          <span>
                            Responsável: {assigneeMember.user?.name || 'N/A'}
                          </span>
                        )}
                        {taskDate && (
                          <span>
                            {task.dueDate ? 'Prazo: ' : 'Data: '}
                            {formatDate(taskDate)}
                          </span>
                        )}
                        {task.location && (
                          <span>Local: {task.location}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!requirePermission('tasks.update', 'Editar tarefas')) return;
                          setEditingTask(task);
                          setShowModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        Editar
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!requirePermission('tasks.delete', 'Excluir tarefas')) return;
                          if (confirm('Tem certeza que deseja deletar esta task?')) {
                            deleteMutation.mutate(task.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Deletar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500 text-lg mb-2">Nenhuma task encontrada</p>
              <p className="text-gray-400 text-sm">
                {myAgenda
                  ? 'Você não tem tasks atribuídas.'
                  : 'Não há tasks no workspace.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <TaskFormModal
          task={editingTask || undefined}
          processId={undefined}
          onClose={() => {
            setShowModal(false);
            setEditingTask(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId], exact: false });
            setShowModal(false);
            setEditingTask(null);
          }}
        />
      )}

      {showDetailModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTask(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId], exact: false });
          }}
        />
      )}
    </div>
  );
}

